/* eslint-disable react/react-in-jsx-scope */
import { AlertCircle, InfoCircle, Lock } from '@saleshandy/icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { NumericFormat } from 'react-number-format';
import { useEffect, useState, useRef, useMemo } from 'react';
import { debounce } from 'lodash';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import classNames from 'classnames';
import { DISABLED_BY_UNKNOWN_TOOLTIP, FilterComponentType } from '../../types';
import { matchSemanticDatePreset } from '../../helpers/helper';
import {
  Placement,
  OverlayTooltip,
} from '../../../../shared/design-system/components/overlay';
import Select from '../../../../shared/design-system/components/select';

const CustomSelect = ({
  subFilter,
  handleQuery,
  isDisabledByUnknown = false,
  isDisabled = false,
  disabledByDependencyTooltip = '',
  pendingFilters,
  applyPendingFilters,
  isLocked = false,
  onLockedClick = undefined,
}) => {
  const effectiveDisabled = isDisabledByUnknown || isDisabled || isLocked;
  const {
    key: filterKey,
    list = [],
    isPrefix = false,
    name = '',
    className = '',
    customInputMode,
    customInputPlaceholder = '',
    tooltipTextInfoCircle = '',
    dropdownPlacement = Placement.Bottom,
  } = subFilter;
  const [selectedOption, setSelectedOption] = useState(null);
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const [customTextValue, setCustomTextValue] = useState('');
  const [startYear, setStartYear] = useState<Date | null>(null);
  const [endYear, setEndYear] = useState<Date | null>(null);
  const [isYearPickerOpen, setIsYearPickerOpen] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState<boolean>(false);
  const [customRangeType, setCustomRangeType] = useState<
    'between' | 'lte' | 'gte' | 'eq'
  >('between');
  const inputRef = useRef(null);
  const dateInputRef = useRef(null);
  const minInputRef = useRef<HTMLInputElement>(null);
  const maxInputRef = useRef<HTMLInputElement>(null);
  const customTextInputRef = useRef<HTMLInputElement>(null);

  const handleQueryRef = useRef(handleQuery);
  const lastWrittenFilterValueRef = useRef<string | null>(null);

  // Update the ref whenever handleQuery changes
  useEffect(() => {
    handleQueryRef.current = handleQuery;
  }, [handleQuery]);

  // Helper: Detect if current filter is a year filter
  const isYearFilter = useMemo(
    () => filterKey === 'company_founded_year' || filterKey === 'founded_year',
    [filterKey],
  );

  // Helper: Detect if current filter is a date filter
  const isDateFilter = useMemo(
    () =>
      filterKey === 'company_funding_date' ||
      filterKey === 'funding_rounds_announced_date' ||
      filterKey === 'last_funding_round_announced_date',
    [filterKey],
  );

  // Helper: Check if filter should show radio buttons (custom numeric/year, not date/text)
  const shouldShowRadioButtons = useMemo(
    () =>
      selectedOption?.key === 'custom' &&
      customInputMode !== 'text' &&
      !isDateFilter,
    [selectedOption, customInputMode, isDateFilter],
  );

  // Radio button labels based on filter type
  const radioOptions = useMemo(() => {
    if (isYearFilter) {
      return [
        { value: 'between', label: 'Is Between' },
        { value: 'lte', label: 'Before Year' },
        { value: 'gte', label: 'After Year' },
        { value: 'eq', label: 'Exact Year' },
      ];
    }
    return [
      { value: 'between', label: 'Is Between' },
      { value: 'lte', label: 'Is less than equal to' },
      { value: 'gte', label: 'Is greater than equal to' },
      { value: 'eq', label: 'Is equal to' },
    ];
  }, [isYearFilter]);

  const isInvalidRange =
    selectedOption?.key === 'custom' &&
    customInputMode !== 'text' &&
    maxValue &&
    minValue &&
    Number(maxValue) < Number(minValue);

  // Debounced function to update pending filters when min/max values change
  const debouncedUpdateFilters = useRef(
    debounce(
      (
        min: string,
        max: string,
        rangeType: 'between' | 'lte' | 'gte' | 'eq',
      ) => {
        if (
          rangeType === 'between' &&
          min &&
          max &&
          Number(max) < Number(min)
        ) {
          return;
        }
        if (min === '' && max === '') {
          lastWrittenFilterValueRef.current = null;
          if (applyPendingFilters) {
            handleQueryRef.current({
              filterKey,
              filterValue: '',
              filterType: FilterComponentType.DROP_DOWN,
              isSearchLeads: false,
              onFiltersUpdated: (updatedFilters) => {
                applyPendingFilters(updatedFilters);
              },
            });
          } else {
            handleQueryRef.current({
              filterKey,
              filterValue: '',
              filterType: FilterComponentType.DROP_DOWN,
              isSearchLeads: false,
            });
          }
        } else {
          let filterValue = '';
          switch (rangeType) {
            case 'between':
              filterValue = `${min || ''}-${max || ''}`;
              break;
            case 'lte':
              filterValue = max ? `-${max}` : '';
              break;
            case 'gte':
              filterValue = min ? `${min}-` : '';
              break;
            case 'eq':
              filterValue = min ? `${min}-${min}` : '';
              break;
            default:
              filterValue = `${min || ''}-${max || ''}`;
          }

          if (rangeType === 'between' && (min || max) && !(min && max)) {
            lastWrittenFilterValueRef.current = filterValue;
          } else {
            lastWrittenFilterValueRef.current = null;
          }
          handleQueryRef.current({
            filterKey,
            filterValue,
            filterType: FilterComponentType.DROP_DOWN,
            isSearchLeads: false,
          });
        }
      },
      1000,
    ),
  ).current;

  const debouncedUpdateTextFilter = useRef(
    debounce((value: string) => {
      const trimmed = (value || '').trim();
      if (trimmed === '' && applyPendingFilters) {
        handleQueryRef.current({
          filterKey,
          filterValue: '',
          filterType: FilterComponentType.DROP_DOWN,
          isSearchLeads: false,
          onFiltersUpdated: (updatedFilters) => {
            applyPendingFilters(updatedFilters);
          },
        });
        return;
      }
      handleQueryRef.current({
        filterKey,
        filterValue: trimmed,
        filterType: FilterComponentType.DROP_DOWN,
        isSearchLeads: false,
      });
    }, 1000),
  ).current;

  // Helper function to convert ISO date string to yyyy-mm-dd format
  const convertISODateToYYYYMMDD = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (!Number.isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    } catch (e) {
      // If parsing fails, return original string
    }
    return dateString;
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const handleCustomSelect = (selectedOption) => {
    setSelectedOption(selectedOption);
    if (selectedOption?.key === 'custom') {
      setMinValue('');
      setMaxValue('');
      setCustomTextValue('');
      setCustomRangeType('between');
      if (
        filterKey === 'company_founded_year' ||
        filterKey === 'founded_year'
      ) {
        setStartYear(null);
        setEndYear(null);
        setIsYearPickerOpen(false);
      }
      if (
        filterKey === 'company_funding_date' ||
        filterKey === 'funding_rounds_announced_date' ||
        filterKey === 'last_funding_round_announced_date'
      ) {
        setStartDate(null);
        setEndDate(null);
        setIsDatePickerOpen(false);
      }
    } else {
      let sizeQueryParam =
        selectedOption?.key === 'select' ? '' : selectedOption?.key;

      // For funding date filters, convert ISO date strings to yyyy-mm-dd format
      if (
        (filterKey === 'company_funding_date' ||
          filterKey === 'funding_rounds_announced_date' ||
          filterKey === 'last_funding_round_announced_date') &&
        sizeQueryParam &&
        sizeQueryParam !== 'all time'
      ) {
        // Check if it's a date range (contains " - ")
        if (sizeQueryParam.includes(' - ')) {
          const [startDateStr, endDateStr] = sizeQueryParam.split(' - ');
          const formattedStart = convertISODateToYYYYMMDD(startDateStr);
          const formattedEnd = convertISODateToYYYYMMDD(endDateStr);
          sizeQueryParam = `${formattedStart} - ${formattedEnd}`;
        } else {
          // Single date - convert if it's ISO format
          const datePattern = /^\d{4}-\d{2}-\d{2}$/;
          if (!datePattern.test(sizeQueryParam)) {
            // Not already in yyyy-mm-dd format, try to convert
            sizeQueryParam = convertISODateToYYYYMMDD(sizeQueryParam);
          }
        }
      }

      if (sizeQueryParam === '' && applyPendingFilters) {
        handleQuery({
          filterKey,
          filterValue: '',
          filterType: FilterComponentType.DROP_DOWN,
          isSearchLeads: false,
          onFiltersUpdated: (updatedFilters) => {
            applyPendingFilters(updatedFilters);
          },
        });
      } else {
        handleQuery({
          filterKey,
          filterValue: sizeQueryParam,
          filterType: FilterComponentType.DROP_DOWN,
          isSearchLeads: false,
        });
      }
    }
  };

  const handleCustomTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e?.target?.value ?? '';
    setCustomTextValue(value);
    if (selectedOption?.key === 'custom' && customInputMode === 'text') {
      debouncedUpdateTextFilter(value.trim());
    }
  };

  const handleCustomRangeTypeChange = (
    newType: 'between' | 'lte' | 'gte' | 'eq',
  ) => {
    setCustomRangeType(newType);
    // Clear current values when switching radio type
    setMinValue('');
    setMaxValue('');
    if (isYearFilter) {
      setStartYear(null);
      setEndYear(null);
    }
    // Don't clear the filter - keep the dropdown as "Custom" with empty value
    // User will fill in the new input, which will then update the filter
  };

  const handleCustomTextBlur = () => {
    debouncedUpdateTextFilter.cancel();
    if (selectedOption?.key === 'custom' && customInputMode === 'text') {
      handleQuery({
        filterKey,
        filterValue: customTextValue.trim(),
        filterType: FilterComponentType.DROP_DOWN,
        isSearchLeads: false,
      });
    }
  };

  const handleMinValueChange = (values: any) => {
    const newMinValue = values?.value || '';
    setMinValue(newMinValue);
    // For 'eq' type, also update maxValue to match
    if (customRangeType === 'eq') {
      setMaxValue(newMinValue);
    }
    // Debounce the filter update
    if (selectedOption?.key === 'custom') {
      const effectiveMaxValue =
        customRangeType === 'eq' ? newMinValue : maxValue;
      debouncedUpdateFilters(newMinValue, effectiveMaxValue, customRangeType);
    }
  };

  const handleMaxValueChange = (values: any) => {
    const newMaxValue = values?.value || '';
    setMaxValue(newMaxValue);
    // Debounce the filter update
    if (selectedOption?.key === 'custom') {
      debouncedUpdateFilters(minValue, newMaxValue, customRangeType);
    }
  };

  const handleBlurInput = () => {
    // Cancel any pending debounced calls
    debouncedUpdateFilters.cancel();

    const isValidRange =
      customRangeType !== 'between' ||
      !minValue ||
      !maxValue ||
      +maxValue >= +minValue;
    if (isValidRange && (minValue || maxValue)) {
      let filterValue = '';
      switch (customRangeType) {
        case 'between':
          filterValue = `${minValue || ''}-${maxValue || ''}`;
          break;
        case 'lte':
          filterValue = maxValue ? `-${maxValue}` : '';
          break;
        case 'gte':
          filterValue = minValue ? `${minValue}-` : '';
          break;
        case 'eq':
          filterValue = minValue ? `${minValue}-${minValue}` : '';
          break;
        default:
          filterValue = `${minValue || ''}-${maxValue || ''}`;
      }
      if (
        customRangeType === 'between' &&
        (minValue || maxValue) &&
        !(minValue && maxValue)
      ) {
        lastWrittenFilterValueRef.current = filterValue;
      } else {
        lastWrittenFilterValueRef.current = null;
      }
      handleQuery({
        filterKey,
        filterValue,
        filterType: FilterComponentType.DROP_DOWN,
        isSearchLeads: false,
      });
    }
  };

  const handleYearRangeChange = (
    dates: Date | [Date | null, Date | null] | null,
  ) => {
    if (Array.isArray(dates)) {
      const [start, end] = dates;
      setStartYear(start);
      setEndYear(end);

      if (start && end) {
        const startYearValue = start.getFullYear().toString();
        const endYearValue = end.getFullYear().toString();
        setMinValue(startYearValue);
        setMaxValue(endYearValue);
        handleQuery({
          filterKey,
          filterValue: `${startYearValue}-${endYearValue}`,
          filterType: FilterComponentType.DROP_DOWN,
          isSearchLeads: true,
        });
        setIsYearPickerOpen(false);
      } else if (start) {
        const startYearValue = start.getFullYear().toString();
        setMinValue(startYearValue);
        setMaxValue('');
      }
    }
  };

  const handleDateRangeChange = (
    dates: Date | [Date | null, Date | null] | null,
  ) => {
    if (Array.isArray(dates)) {
      const [start, end] = dates;
      setStartDate(start);
      setEndDate(end);

      if (start && end) {
        // Format dates as yyyy-mm-dd
        const formatDate = (date: Date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const formattedStart = formatDate(start);
        const formattedEnd = formatDate(end);
        const dateRangeValue = `${formattedStart} - ${formattedEnd}`;

        handleQuery({
          filterKey,
          filterValue: dateRangeValue,
          filterType: FilterComponentType.DROP_DOWN,
          isSearchLeads: true,
        });
        setIsDatePickerOpen(false);
      } else if (start) {
        // Only start date selected, don't save yet
        setStartDate(start);
      }
    }
  };

  const handleMinInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && selectedOption?.key === 'custom') {
      // Move focus to max input
      e.preventDefault();
      maxInputRef.current?.focus();
    }
  };

  const handleMaxInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && selectedOption?.key === 'custom') {
      e.preventDefault();

      // Validate values
      const isValidRange =
        customRangeType !== 'between' ||
        !minValue ||
        !maxValue ||
        +maxValue >= +minValue;
      const hasValues = minValue || maxValue;

      if (hasValues && isValidRange) {
        // Cancel any pending debounced updates
        debouncedUpdateFilters.cancel();

        let filterValue = '';
        switch (customRangeType) {
          case 'between':
            filterValue = `${minValue || ''}-${maxValue || ''}`;
            break;
          case 'lte':
            filterValue = maxValue ? `-${maxValue}` : '';
            break;
          case 'gte':
            filterValue = minValue ? `${minValue}-` : '';
            break;
          case 'eq':
            filterValue = minValue ? `${minValue}-${minValue}` : '';
            break;
          default:
            filterValue = `${minValue || ''}-${maxValue || ''}`;
        }

        if (
          customRangeType === 'between' &&
          (minValue || maxValue) &&
          !(minValue && maxValue)
        ) {
          lastWrittenFilterValueRef.current = filterValue;
        } else {
          lastWrittenFilterValueRef.current = null;
        }

        // Update filters and trigger search if applyPendingFilters is available
        if (applyPendingFilters) {
          handleQuery({
            filterKey,
            filterValue,
            filterType: FilterComponentType.DROP_DOWN,
            isSearchLeads: false,
            onFiltersUpdated: (updatedFilters) => {
              // Apply the updated filters immediately to trigger search API
              applyPendingFilters(updatedFilters);
            },
          });
        } else {
          // Fallback to blur handler if applyPendingFilters is not available
          handleBlurInput();
        }
      }
    }
  };

  useEffect(() => {
    // eslint-disable-next-line no-restricted-globals
    const queryParams = new URLSearchParams(location.search);
    // Check pendingFilters first (if available), otherwise use URL params
    const paramsToRead = pendingFilters || queryParams;
    const filterValue = paramsToRead.get(filterKey);

    if (paramsToRead.has(filterKey) && filterValue) {
      // For company_funding_date, try to match with both ISO format and yyyy-mm-dd format
      let matchingOption = null;
      if (filterKey === 'company_funding_date') {
        // Try exact match first
        matchingOption = list.find(
          (option) =>
            option.key === filterValue ||
            option.key.trim() === filterValue.trim(),
        );

        // If not found and it's a date range in yyyy-mm-dd format, try to match with ISO format options
        if (!matchingOption && filterValue.includes(' - ')) {
          const [startDateStr, endDateStr] = filterValue.split(' - ');
          const datePattern = /^\d{4}-\d{2}-\d{2}$/;
          if (datePattern.test(startDateStr) && datePattern.test(endDateStr)) {
            // Try to find matching option by converting yyyy-mm-dd back to ISO for comparison
            // or find by comparing date values
            matchingOption = list.find((option) => {
              if (
                option.key === 'all time' ||
                option.key === 'custom' ||
                option.key === 'select'
              ) {
                return false;
              }
              if (option.key.includes(' - ')) {
                try {
                  const [optionStart, optionEnd] = option.key.split(' - ');
                  const optionStartDate = new Date(optionStart);
                  const optionEndDate = new Date(optionEnd);
                  const filterStartDate = new Date(startDateStr);
                  const filterEndDate = new Date(endDateStr);

                  if (
                    !Number.isNaN(optionStartDate.getTime()) &&
                    !Number.isNaN(optionEndDate.getTime()) &&
                    !Number.isNaN(filterStartDate.getTime()) &&
                    !Number.isNaN(filterEndDate.getTime())
                  ) {
                    const oStart = optionStartDate.toDateString();
                    const oEnd = optionEndDate.toDateString();
                    const fStart = filterStartDate.toDateString();
                    const fEnd = filterEndDate.toDateString();
                    // Allow swapped min/max from older stored searches
                    return (
                      (oStart === fStart && oEnd === fEnd) ||
                      (oStart === fEnd && oEnd === fStart)
                    );
                  }
                } catch (e) {
                  // Ignore parsing errors
                }
              }
              return false;
            });
          }
        }
      } else if (
        filterKey === 'last_funding_round_announced_date' ||
        filterKey === 'funding_rounds_announced_date'
      ) {
        // Try exact match first (e.g., key stored as 'last_90_days')
        matchingOption = list.find(
          (option) =>
            option.key === filterValue ||
            option.key.trim() === filterValue.trim(),
        );
        // If not found and value is a yyyy-mm-dd date range, reverse-map to semantic preset
        if (!matchingOption && filterValue.includes(' - ')) {
          const [filterMinStr, filterMaxStr] = filterValue
            .split(' - ')
            .map((v) => v.trim());
          const filterMin = new Date(filterMinStr);
          const filterMax = new Date(filterMaxStr);
          if (
            !Number.isNaN(filterMin.getTime()) &&
            !Number.isNaN(filterMax.getTime())
          ) {
            matchingOption = matchSemanticDatePreset(
              filterMin,
              filterMax,
              list,
            );
          }
        }
      } else {
        // For other filters, use standard matching
        matchingOption = list.find(
          (option) =>
            option.key === filterValue ||
            option.key.trim() === filterValue.trim(),
        );
      }

      if (matchingOption) {
        // Found a matching option - it's not a custom selection
        setSelectedOption(matchingOption);
        setMinValue('');
        setMaxValue('');
        setCustomTextValue('');
        if (
          filterKey === 'company_founded_year' ||
          filterKey === 'founded_year'
        ) {
          setStartYear(null);
          setEndYear(null);
          setIsYearPickerOpen(false);
        }
      } else {
        // No matching option found - it's a custom selection
        setSelectedOption({ key: 'custom', label: 'Custom' });

        if (
          (filterKey === 'company_funding_date' ||
            filterKey === 'funding_rounds_announced_date' ||
            filterKey === 'last_funding_round_announced_date') &&
          filterValue.includes(' - ')
        ) {
          // Handle custom date range for funding date filters
          const [startDateStr, endDateStr] = filterValue.split(' - ');
          const datePattern = /^\d{4}-\d{2}-\d{2}$/;
          if (datePattern.test(startDateStr) && datePattern.test(endDateStr)) {
            try {
              const start = new Date(startDateStr);
              const end = new Date(endDateStr);
              if (
                !Number.isNaN(start.getTime()) &&
                !Number.isNaN(end.getTime())
              ) {
                setStartDate(start);
                setEndDate(end);
              } else {
                setStartDate(null);
                setEndDate(null);
              }
            } catch (e) {
              setStartDate(null);
              setEndDate(null);
            }
          } else {
            setStartDate(null);
            setEndDate(null);
          }
        } else if (
          filterKey === 'company_founded_year' ||
          filterKey === 'founded_year'
        ) {
          // Handle custom year range for founded year filters
          const [min, max] = (filterValue || '').split('-');
          setMinValue(min || '');
          setMaxValue(max || '');

          if (min && max) {
            // between or eq case: both values exist
            const minYear = parseInt(min, 10);
            const maxYear = parseInt(max, 10);
            if (!Number.isNaN(minYear) && !Number.isNaN(maxYear)) {
              setStartYear(new Date(minYear, 0, 1));
              setEndYear(new Date(maxYear, 0, 1));
            }
          } else if (!min && max) {
            // lte case: only max value (Before Year)
            const maxYear = parseInt(max, 10);
            if (!Number.isNaN(maxYear)) {
              setEndYear(new Date(maxYear, 0, 1));
              setStartYear(null);
            }
          } else if (min && !max) {
            // gte case: only min value (After Year)
            const minYear = parseInt(min, 10);
            if (!Number.isNaN(minYear)) {
              setStartYear(new Date(minYear, 0, 1));
              setEndYear(null);
            }
          } else {
            setStartYear(null);
            setEndYear(null);
            setIsYearPickerOpen(false);
          }
        } else if (customInputMode === 'text') {
          // Handle custom text input for dropdowns (e.g. job title)
          setCustomTextValue(filterValue || '');
          setMinValue('');
          setMaxValue('');
        } else {
          // Handle other custom selections (numeric ranges)
          const [min, max] = (filterValue || '').split('-');
          setMinValue(min || '');
          setMaxValue(max || '');
          setCustomTextValue('');

          const isOurBetweenPartialWrite =
            filterValue === lastWrittenFilterValueRef.current;
          if (min && max) {
            if (min === max) {
              setCustomRangeType('eq');
            } else {
              setCustomRangeType('between');
            }
            lastWrittenFilterValueRef.current = null;
          } else if (!min && max) {
            if (!isOurBetweenPartialWrite) {
              setCustomRangeType('lte');
            }
            lastWrittenFilterValueRef.current = null;
          } else if (min && !max) {
            if (!isOurBetweenPartialWrite) {
              setCustomRangeType('gte');
            }
            lastWrittenFilterValueRef.current = null;
          } else {
            setCustomRangeType('between');
            lastWrittenFilterValueRef.current = null;
          }
        }
      }
    } else {
      setSelectedOption(null);
      setMinValue('');
      setMaxValue('');
      setCustomTextValue('');
      if (
        filterKey === 'company_founded_year' ||
        filterKey === 'founded_year'
      ) {
        setStartYear(null);
        setEndYear(null);
        setIsYearPickerOpen(false);
      }
      if (
        filterKey === 'company_funding_date' ||
        filterKey === 'funding_rounds_announced_date' ||
        filterKey === 'last_funding_round_announced_date'
      ) {
        setStartDate(null);
        setEndDate(null);
        setIsDatePickerOpen(false);
      }
    }
    // eslint-disable-next-line no-restricted-globals
  }, [location?.search, filterKey, list, pendingFilters]);

  // Cleanup debounce on unmount
  useEffect(
    () => () => {
      debouncedUpdateFilters.cancel();
      debouncedUpdateTextFilter.cancel();
    },
    [debouncedUpdateFilters, debouncedUpdateTextFilter],
  );

  // Focus the correct input when Custom is selected or range type changes
  useEffect(() => {
    if (selectedOption?.key !== 'custom' || !shouldShowRadioButtons) {
      return undefined;
    }
    const id = requestAnimationFrame(() => {
      if (isYearFilter) {
        inputRef.current?.focus();
      } else if (customRangeType === 'lte') {
        maxInputRef.current?.focus();
      } else {
        minInputRef.current?.focus();
      }
    });
    return () => cancelAnimationFrame(id);
  }, [
    selectedOption?.key,
    customRangeType,
    shouldShowRadioButtons,
    isYearFilter,
  ]);

  const getTooltip = (text) => (
    <Tooltip id="popover-basic" className="bs-tooltip-inner">
      {text}
    </Tooltip>
  );

  const renderFilterContent = () => (
    <div className={effectiveDisabled ? 'disabled-filter-content' : ''}>
      <Select
        options={list}
        selectedOptionKey={selectedOption?.key || undefined}
        optionRenderer={(option: any) => <span>{option?.label}</span>}
        onChange={([option]) => {
          if (!effectiveDisabled) {
            handleCustomSelect(option);
          }
        }}
        selectedOptionRenderer={([option]) => <span>{option?.label}</span>}
        placeholder={subFilter?.placeholder || 'Select'}
        className="dropdown-filter-select"
        placement={dropdownPlacement}
      />

      {shouldShowRadioButtons && !isYearFilter && (
        <div className="custom-range-radio-group">
          {radioOptions.map((option) => {
            const radioId = `custom-range-${filterKey}-${option.value}`;
            const isSelected = customRangeType === option.value;
            return (
              <div key={option.value} className="custom-range-radio-wrapper">
                <label
                  htmlFor={radioId}
                  className={classNames('custom-range-radio-option', {
                    'radio-selected': isSelected,
                  })}
                >
                  <input
                    id={radioId}
                    type="radio"
                    name={`custom-range-${filterKey}`}
                    value={option.value}
                    checked={isSelected}
                    onChange={() =>
                      handleCustomRangeTypeChange(option.value as any)
                    }
                    disabled={effectiveDisabled}
                  />
                  <span className="radio-label">{option.label}</span>
                </label>

                {/* Inline input for "Is Between" - show Min and Max */}
                {isSelected && option.value === 'between' && (
                  <div
                    className={classNames('input-group radio-inline-input', {
                      'has-error': isInvalidRange,
                    })}
                  >
                    <div className="d-flex min-input-container">
                      {isPrefix && (
                        <div className="input-group-prepend">
                          <span className="input-group-text regular-2 blue-txt-15 gray-19">
                            $
                          </span>
                        </div>
                      )}
                      <NumericFormat
                        getInputRef={minInputRef}
                        value={minValue}
                        placeholder="Min"
                        autoComplete="off"
                        maxLength={15}
                        disabled={effectiveDisabled}
                        thousandSeparator
                        allowNegative={false}
                        className={classNames(
                          'form-control blue-txt-15 regular-2 mr-2',
                          {
                            'without-prefix': !isPrefix,
                            'is-invalid': isInvalidRange,
                          },
                        )}
                        onValueChange={
                          effectiveDisabled ? undefined : handleMinValueChange
                        }
                        onKeyPress={
                          effectiveDisabled ? undefined : handleMinInputKeyPress
                        }
                      />
                    </div>
                    <div className="d-flex max-input-container">
                      {isPrefix && (
                        <div className="input-group-prepend">
                          <span
                            className={classNames(
                              'input-group-text regular-2 blue-txt-15 gray-19',
                              {
                                'error-prefix-input': isInvalidRange,
                              },
                            )}
                          >
                            $
                          </span>
                        </div>
                      )}
                      <div className="max-input">
                        <NumericFormat
                          getInputRef={maxInputRef}
                          name="value"
                          placeholder="Max"
                          autoComplete="off"
                          value={maxValue}
                          allowNegative={false}
                          maxLength={15}
                          disabled={effectiveDisabled}
                          thousandSeparator
                          onValueChange={
                            effectiveDisabled ? undefined : handleMaxValueChange
                          }
                          className={classNames(
                            'form-control blue-txt-15 regular-2',
                            {
                              'without-prefix': !isPrefix,
                              'is-invalid': isInvalidRange,
                            },
                          )}
                          onKeyPress={
                            effectiveDisabled || isInvalidRange
                              ? undefined
                              : handleMaxInputKeyPress
                          }
                        />
                        {isInvalidRange && (
                          <div className="error-message">
                            <OverlayTrigger
                              placement={Placement.Bottom}
                              overlay={getTooltip(
                                'Max cannot be less than Min',
                              )}
                            >
                              <AlertCircle
                                className="red-txt-16"
                                width={16}
                                height={16}
                              />
                            </OverlayTrigger>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Inline input for "lte", "gte", "eq" - show single input */}
                {isSelected &&
                  (option.value === 'lte' ||
                    option.value === 'gte' ||
                    option.value === 'eq') && (
                    <div className="input-group radio-inline-input">
                      <div className="d-flex single-input-container">
                        {isPrefix && (
                          <div className="input-group-prepend">
                            <span className="input-group-text regular-2 blue-txt-15 gray-19">
                              $
                            </span>
                          </div>
                        )}
                        <NumericFormat
                          getInputRef={
                            option.value === 'gte' || option.value === 'eq'
                              ? minInputRef
                              : maxInputRef
                          }
                          value={
                            option.value === 'gte' || option.value === 'eq'
                              ? minValue
                              : maxValue
                          }
                          placeholder={(() => {
                            if (option.value === 'lte') return 'Max';
                            if (option.value === 'gte') return 'Min';
                            return 'Value';
                          })()}
                          autoComplete="off"
                          maxLength={15}
                          disabled={effectiveDisabled}
                          thousandSeparator
                          allowNegative={false}
                          className={classNames(
                            'form-control blue-txt-15 regular-2',
                            {
                              'without-prefix': !isPrefix,
                            },
                          )}
                          onValueChange={(() => {
                            if (effectiveDisabled) return undefined;
                            if (
                              option.value === 'gte' ||
                              option.value === 'eq'
                            ) {
                              return handleMinValueChange;
                            }
                            return handleMaxValueChange;
                          })()}
                          onKeyPress={(() => {
                            if (effectiveDisabled) return undefined;
                            if (option.value === 'gte') {
                              return handleMinInputKeyPress;
                            }
                            return handleMaxInputKeyPress;
                          })()}
                        />
                      </div>
                    </div>
                  )}
              </div>
            );
          })}
        </div>
      )}

      {shouldShowRadioButtons && isYearFilter && (
        <div className="custom-range-radio-group">
          {radioOptions.map((option) => {
            const radioId = `custom-range-${filterKey}-${option.value}`;
            const isSelected = customRangeType === option.value;
            return (
              <div key={option.value} className="custom-range-radio-wrapper">
                <label
                  htmlFor={radioId}
                  className={classNames('custom-range-radio-option', {
                    'radio-selected': isSelected,
                  })}
                >
                  <input
                    id={radioId}
                    type="radio"
                    name={`custom-range-${filterKey}`}
                    value={option.value}
                    checked={isSelected}
                    onChange={() =>
                      handleCustomRangeTypeChange(option.value as any)
                    }
                    disabled={effectiveDisabled}
                  />
                  <span className="radio-label">{option.label}</span>
                </label>

                {/* Inline year picker for the selected option */}
                {isSelected && (
                  <div
                    className={classNames(
                      'input-group radio-inline-input year-picker-container',
                      {
                        'has-error': isInvalidRange,
                      },
                    )}
                  >
                    <DatePicker
                      selected={option.value === 'lte' ? endYear : startYear}
                      onChange={
                        option.value === 'between'
                          ? handleYearRangeChange
                          : (date: Date | null) => {
                              if (
                                option.value === 'eq' ||
                                option.value === 'gte'
                              ) {
                                setStartYear(date);
                                if (date) {
                                  const yearValue = date
                                    .getFullYear()
                                    .toString();
                                  setMinValue(yearValue);
                                  if (option.value === 'eq') {
                                    setMaxValue(yearValue);
                                  } else {
                                    setMaxValue('');
                                  }
                                  handleQuery({
                                    filterKey,
                                    filterValue:
                                      option.value === 'eq'
                                        ? `${yearValue}-${yearValue}`
                                        : `${yearValue}-`,
                                    filterType: FilterComponentType.DROP_DOWN,
                                    isSearchLeads: true,
                                  });
                                }
                              } else if (option.value === 'lte') {
                                setEndYear(date);
                                if (date) {
                                  const yearValue = date
                                    .getFullYear()
                                    .toString();
                                  setMaxValue(yearValue);
                                  setMinValue('');
                                  handleQuery({
                                    filterKey,
                                    filterValue: `-${yearValue}`,
                                    filterType: FilterComponentType.DROP_DOWN,
                                    isSearchLeads: true,
                                  });
                                }
                              }
                              setIsYearPickerOpen(false);
                            }
                      }
                      startDate={
                        option.value === 'between' ? startYear : undefined
                      }
                      endDate={option.value === 'between' ? endYear : undefined}
                      selectsRange={option.value === 'between'}
                      showYearPicker
                      dateFormat="yyyy"
                      placeholderText={
                        option.value === 'between'
                          ? 'Select year range'
                          : 'Select year'
                      }
                      open={isYearPickerOpen}
                      onInputClick={() =>
                        !effectiveDisabled && setIsYearPickerOpen(true)
                      }
                      onClickOutside={() => setIsYearPickerOpen(false)}
                      minDate={new Date(1900, 0, 1)}
                      maxDate={new Date(new Date().getFullYear(), 0, 1)}
                      className="year-range-picker"
                      disabled={effectiveDisabled}
                      customInput={
                        <input
                          ref={inputRef}
                          value={(() => {
                            if (option.value === 'between') {
                              return startYear
                                ? startYear.getFullYear().toString()
                                : '';
                            }
                            if (option.value === 'lte') {
                              return endYear
                                ? endYear.getFullYear().toString()
                                : '';
                            }
                            return startYear
                              ? startYear.getFullYear().toString()
                              : '';
                          })()}
                          placeholder={
                            option.value === 'between'
                              ? 'Select year range'
                              : 'Select year'
                          }
                          readOnly
                          disabled={effectiveDisabled}
                          className={classNames(
                            'form-control blue-txt-15 regular-2 mr-2',
                            {
                              'without-prefix': !isPrefix,
                            },
                          )}
                          autoComplete="off"
                          maxLength={0}
                        />
                      }
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {selectedOption?.key === 'custom' &&
        customInputMode === 'text' &&
        filterKey !== 'company_founded_year' &&
        filterKey !== 'founded_year' &&
        filterKey !== 'company_funding_date' &&
        filterKey !== 'funding_rounds_announced_date' &&
        filterKey !== 'last_funding_round_announced_date' && (
          <div className="input-group company-custom-text-input">
            <input
              ref={customTextInputRef}
              type="text"
              autoComplete="off"
              disabled={effectiveDisabled}
              className="form-control blue-txt-15 regular-2"
              placeholder={customInputPlaceholder || 'Enter value'}
              value={customTextValue}
              onChange={effectiveDisabled ? undefined : handleCustomTextChange}
              onBlur={effectiveDisabled ? undefined : handleCustomTextBlur}
              maxLength={200}
            />
          </div>
        )}

      {selectedOption?.key === 'custom' &&
        (filterKey === 'company_funding_date' ||
          filterKey === 'funding_rounds_announced_date' ||
          filterKey === 'last_funding_round_announced_date') && (
          <div
            className={classNames('input-group', {
              'has-error': isInvalidRange,
            })}
          >
            <DatePicker
              selected={startDate}
              onChange={handleDateRangeChange}
              startDate={startDate}
              endDate={endDate}
              selectsRange
              dateFormat="MMM d, yyyy"
              placeholderText="Select date range"
              open={isDatePickerOpen}
              onInputClick={() =>
                !effectiveDisabled && setIsDatePickerOpen(true)
              }
              onClickOutside={() => {
                if (startDate && !endDate) {
                  const formatDate = (date: Date) => {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                  };
                  const formatted = formatDate(startDate);
                  setEndDate(startDate);
                  handleQuery({
                    filterKey,
                    filterValue: `${formatted} - ${formatted}`,
                    filterType: FilterComponentType.DROP_DOWN,
                    isSearchLeads: true,
                  });
                }
                setIsDatePickerOpen(false);
              }}
              maxDate={new Date()}
              className="date-range-picker"
              disabled={effectiveDisabled}
              customInput={
                <input
                  ref={dateInputRef}
                  value={(() => {
                    if (startDate && endDate) {
                      return `${startDate.toISOString().split('T')[0]} - ${
                        endDate.toISOString().split('T')[0]
                      }`;
                    }
                    if (startDate) {
                      return startDate.toISOString().split('T')[0];
                    }
                    return '';
                  })()}
                  placeholder="Select date range"
                  readOnly
                  disabled={effectiveDisabled}
                  className={classNames('form-control blue-txt-15 regular-2', {
                    'without-prefix': !isPrefix,
                  })}
                  autoComplete="off"
                  maxLength={200}
                />
              }
            />
          </div>
        )}
    </div>
  );

  const tooltipText =
    disabledByDependencyTooltip !== ''
      ? disabledByDependencyTooltip
      : DISABLED_BY_UNKNOWN_TOOLTIP;

  if (isLocked) {
    return (
      <div
        className={`dropdown-filter ${className} filter-subfilter-locked`}
        onClick={onLockedClick}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onLockedClick?.();
          }
        }}
      >
        {name && (
          <p className="blue-txt-15 label">
            {name}
            <Lock
              width={14}
              height={14}
              color="#F59E0B"
              className="subfilter-lock-icon"
            />
          </p>
        )}
        <div style={{ pointerEvents: 'none' }}>{renderFilterContent()}</div>
      </div>
    );
  }

  return (
    <div
      className={`dropdown-filter ${className} ${
        effectiveDisabled ? 'filter-disabled-by-unknown' : ''
      }`}
    >
      {name && (
        <p className="blue-txt-15 label">
          {name}
          {tooltipTextInfoCircle !== '' && (
            <OverlayTooltip
              placement={Placement.Top}
              text={tooltipTextInfoCircle}
              className="bs-tooltip-inner info-circle-tooltip"
            >
              <InfoCircle color="#6b7280" />
            </OverlayTooltip>
          )}
        </p>
      )}
      {effectiveDisabled ? (
        <OverlayTooltip
          placement={Placement.Top}
          text={tooltipText}
          className="bs-tooltip-inner disabled-by-unknown-tooltip"
        >
          <div className="disabled-filter-wrapper">{renderFilterContent()}</div>
        </OverlayTooltip>
      ) : (
        renderFilterContent()
      )}
    </div>
  );
};

export default CustomSelect;
